<template>
  <div ref="componentEl">
    <aw-section-wrapper v-if="!isHidden" :has-padding="false">
      <template #content="{ contentEvents: { contentLoading } }">
        <aw-tab-carousel
          :heading-tag="headingTag"
          :main-title="mainTitle"
          :empty-placeholder="emptyPlaceholder"
          :tabs="tabs"
          :breakpoints="breakpoints"
          :slides="slides"
          tab-key="id"
          slide-key="id"
          :class="[$style.tabCarousel]"
          @tab-opened="selectTab(contentLoading, $event.newTabItem)"
          @render-sub-slides="onRender"
        >
          <template #slide(all)(all)="{ slide, tabItem }">
            <div
              :class="['awProductCardPlaceholder', $style.tabCarouselCardPlaceholder]"
              :data-product-id="!slide.isPlaceholder ? slide.id : undefined"
              :data-product-index="!slide.isPlaceholder ? getGlobalProductIndex(slide, tabItem) : undefined"
            >
              <aw-product-card-version2
                v-if="!slide.isPlaceholder && productCardSize"
                :key="slide.id"
                :heading-tag="mainTitle ? childrenHeadingTag : headingTag"
                :product-data="slide"
                :card-size="productCardSize"
                :has-compare="false"
                :opened-details="false"
                :bordered="isOnWhiteCard"
                :class="$style.tabCarouselItem"
                :list-index="getGlobalProductIndex(slide, tabItem)"
                :item-list-id="getItemListId(getGlobalProductIndex(slide, tabItem))"
                :item-list-name="getItemListName()"
              />
            </div>
          </template>
        </aw-tab-carousel>
      </template>
    </aw-section-wrapper>
  </div>
</template>

<script setup>
  import { defineAsyncComponent, nextTick, ref, computed, onMounted, onUnmounted } from 'vue';
  import { useNuxtApp } from 'nuxt/app';
  import { getProductCardSizeV2 } from '~~/common/utils/productCard';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface';
  import { usePromotionStore } from '~~/shop/app/stores/promotion';
  import { useProductStore } from '~~/common/stores/product';
  import { useUserStore } from '~~/common/stores/user';
  import { useProductListAnalytics } from '~~/common/composables/useProductListAnalytics';
  import { calculateHeadingChildren } from '~~/common/utils/headingTag';
  import {
    HT_RECOMMENDATION_CAROUSEL_PRODUCT,
    HT_LAST_VIEWED_PRODUCTS,
  } from '~~/common/config/ContentPageTypeConfig';

  const AwTabCarousel = defineAsyncComponent(() => import('~~/common/components/AwTabCarousel'));
  const AwProductCardVersion2 = defineAsyncComponent(() => import('~~/common/components/Common/AwProductCardVersion2'));
  const AwSectionWrapper = defineAsyncComponent(() => import('~~/common/components/Page/Common/AwSectionWrapper'));

  defineOptions({
    name: 'AwRecommendationCarousel',
    inheritAttrs: false,
  });

  const props = defineProps({
    content: {
      type: Object,
      required: true,
    },
    inlineCmsBreakpoints: {
      type: Array,
      default: null,
    },
    headingTag: {
      type: String,
      default: 'h2',
    },
    analyticsBannerInteraction: {
      type: [Object, Symbol],
      default: null,
    },
    analyticsMeasurable: {
      type: Boolean,
      default: false,
    },
    isOnWhiteCard: {
      type: Boolean,
      default: false,
    },
  });

  const { $awEvents, $awt, $logger } = useNuxtApp();
  const userInterfaceStore = useUserInterfaceStore();

  const componentEl = ref(null);
  const tabs = [{
    id: 'recommendation_carousel',
    tabTitle: '',
  }];
  const slides = ref(Object.fromEntries(tabs.map(e => [e?.id, []])));
  const selectedTabId = ref(null);
  const mainTitle = ref(null);
  const productCardSize = ref(null);

  const screenRange = computed(() => userInterfaceStore.mediaQueries);
  const contentType = computed(() => props.content?.type);

  const emptyPlaceholder = computed(() => {
    return $awt(contentType.value === HT_LAST_VIEWED_PRODUCTS
      ? 'aw.cms.last_viewed_products.fallback.text'
      : 'aw.cms.recommendation_carousel.fallback.text',
    );
  });

  const promotionName = computed(() => {
    return $awt(contentType.value === HT_LAST_VIEWED_PRODUCTS
      ? 'aw.cms.last_viewed_products.analytics.promotion_name'
      : 'aw.cms.recommendation_carousel.analytics.promotion_name',
    );
  });

  const isHidden = computed(() => {
    return contentType.value === HT_LAST_VIEWED_PRODUCTS && !useUserStore().isEcomUser;
  });

  const breakpoints = computed(() => {
    return props.inlineCmsBreakpoints || [
      {
        at: screenRange.value['desktop-large-min'],
        count: 5,
      },
      {
        at: screenRange.value['desktop-small-min'],
        count: 4,
      },
      {
        at: screenRange.value['tablet-min'],
        count: 3,
      },
      {
        at: true,
        count: 'auto',
      },
    ];
  });

  const childrenHeadingTag = computed(() => {
    const childrenHeading = calculateHeadingChildren(props.headingTag);
    if (childrenHeading) {
      return childrenHeading;
    } else {
      $logger.warn('childrenHeadingTag can not be determined');
      return props.headingTag;
    }
  });

  const {
    getItemListId,
    getItemListName,
    observeProductCards,
  } = useProductListAnalytics({
    componentEl,
    products: slides,
    analyticsBannerInteraction: props.analyticsBannerInteraction,
    itemListName: promotionName.value,
    getProductIndex: (product) => {
      const tabId = selectedTabId.value;
      if (!tabId || !slides.value[tabId]) {
        return 0;
      }
      const productList = slides.value[tabId];
      const index = productList.findIndex(p => p?.id === product?.id);
      return index !== -1 ? index : 0;
    },
  });

  async function onRender () {
    if (!productCardSize.value) {
      await nextTick();
      await nextTick();
      if (!productCardSize.value) {
        setCardSize();
      }
    }
  }

  function setCardSize () {
    const placeholder = componentEl.value?.querySelector?.('.awProductCardPlaceholder');
    const clientWidth = placeholder?.clientWidth;
    const pcs = getProductCardSizeV2(clientWidth);
    if (pcs) {
      productCardSize.value = pcs;
    }
  }

  function initSlides () {
    const placeholders = Array(breakpoints.value.find(b => b.at).count).fill();
    tabs.forEach((tab) => {
      slides.value[tab.id] = placeholders.map((_u, idx) => ({
        id: idx,
        isPlaceholder: true,
      }));
    });
  }

  async function selectTab (updateLoadingState, { id }) {
    try {
      const {
        fetchRecommendedProducts,
        fetchRecentlyViewedProducts,
        fetchRelatedProducts,
      } = usePromotionStore();

      selectedTabId.value = id;
      if (slides.value[id]?.length && !slides.value[id][0]?.isPlaceholder) {
        return;
      }

      updateLoadingState(true);
      let results;
      if (contentType.value === HT_RECOMMENDATION_CAROUSEL_PRODUCT) {
        results = await fetchRelatedProducts(useProductStore().data?.id);
      } else if (contentType.value === HT_LAST_VIEWED_PRODUCTS) {
        results = await fetchRecentlyViewedProducts();
      } else {
        results = await fetchRecommendedProducts(props.content.contentId);
      }

      slides.value[id] = results;

      await nextTick();
      observeProductCards();
    } catch (e) {
      $logger.error(e);
    } finally {
      updateLoadingState(false);
    }
  }

  function getGlobalProductIndex (slide, tabItem) {
    const tabId = tabItem?.id;
    if (!tabId || !slides.value[tabId]) {
      return 0;
    }

    const productList = slides.value[tabId];
    const index = productList.findIndex(p => p?.id === slide?.id);

    return index !== -1 ? index : 0;
  }

  onMounted(() => {
    $awEvents.addEvent('throttled-resize', setCardSize);
    initSlides();
  });

  onUnmounted(() => {
    $awEvents.removeEvent('throttled-resize', setCardSize);
  });
</script>

<style module lang="scss" rel="stylesheet/scss">

.tabCarousel {
  --awTabItemPadding: 14px 16px;
  --awTabCarouselParentPaddingTop: 0;

  @include tablet(min) {
    --awTabItemPadding: 12px 16px;
  }
}

.tabCarouselItem {
  @include mobile(max) {
    width: 200px;
    max-width: 200px;
  }
}

.tabCarouselPlaceHolder {
  margin-top: 2px;
}
</style>
